/**
 * Save a picked image again as a JPEG, through a canvas — the estate's ONE
 * canvas re-encode (ARCH-11), used by <ImageCapture> (receipts) and the
 * registrar dashboard's front-page photos (cca-9f0kn S3b).
 *
 *   · createImageBitmap reads the picture the right way up (`from-image`), so
 *     the camera's orientation tag can be dropped without turning the photo;
 *   · it is drawn onto a fresh canvas at most `maxEdge` px on its long edge
 *     (never enlarged), over white — so a transparent PNG comes out on white,
 *     not black — and written out as a JPEG data URL at `quality`: nothing of
 *     the original file but its pixels survives, so no location, no device, no
 *     other camera metadata (Lazo H1, cca-9f0kn);
 *   · what is not an image is REFUSED with a typed reason, a PDF included: a
 *     caller that takes PDFs (ImageCapture) decides that before it calls.
 *
 * Browser-only (createImageBitmap, document). Never throws for a bad file: a
 * file the browser cannot decode is `unreadable`, a canvas without a 2D context
 * is `no_canvas`.
 */

export interface ReencodeOptions {
  /** The long edge of the result, at most, in pixels. */
  maxEdge: number;
  /** JPEG quality, 0–1. */
  quality: number;
}

export type ReencodeResult =
  | {
      ok: true;
      /** `data:image/jpeg;base64,…` — ready to preview (a data: image) or to send. */
      dataUrl: string;
      /** The size of the result, after shrinking. */
      width: number;
      height: number;
    }
  | { ok: false; reason: 'pdf' | 'not_an_image' | 'unreadable' | 'no_canvas' };

/** The file to re-encode: a Blob (its type), and a File's name when there is one. */
export type ReencodeSource = Blob & { readonly name?: string };

export async function reencodeToJpeg(
  file: ReencodeSource,
  { maxEdge, quality }: ReencodeOptions,
): Promise<ReencodeResult> {
  // A browser that names no type ('' — a HEIC on Windows, say) is let through to
  // the decoder, which decides; only then does a .pdf name count as a PDF.
  const untyped = file.type === '';
  if (file.type === 'application/pdf' || (untyped && /\.pdf$/i.test(file.name ?? ''))) {
    return { ok: false, reason: 'pdf' };
  }
  if (!untyped && !file.type.startsWith('image/')) return { ok: false, reason: 'not_an_image' };

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return { ok: false, reason: 'unreadable' };
  }
  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return { ok: false, reason: 'no_canvas' };
    // JPEG has no transparency: a transparent pixel left on a bare canvas comes
    // out black. White first, as paper, then the picture over it (LeC r1).
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);
    return { ok: true, dataUrl: canvas.toDataURL('image/jpeg', quality), width, height };
  } finally {
    bitmap.close();
  }
}
